import { Button } from '@mui/material'
import { useTranslate } from 'ra-core'
import { useState } from 'react'
import { Confirm, useRecordContext, useResourceContext } from 'react-admin'

import { getResourceByName } from '../resources'
import { useRunResourceAction } from '../resources/common/hooks'
import { useSmallScreen } from '../utils/theme'

const funcOrValue = (object, ...args) => (typeof object === 'function' ? object(...args) : object)

const ActionButton = ({ action, children, ...props }) => {
  const record = useRecordContext()
  const isSmallScreen = useSmallScreen()
  const resource = useResourceContext()
  const translate = useTranslate()

  const resourceConfig = getResourceByName(resource)
  const actionConfig = resourceConfig.options.actions?.[action]
  const actionTranslationBase = `resources.${resource}.actions.${actionConfig?.name}`
  const actionLabel = `${actionTranslationBase}.` + (isSmallScreen ? 'shortLabel' : 'label')
  const confirm = funcOrValue(actionConfig?.confirm, { record })
  const SuccessDialog = actionConfig?.successDialog
  const Icon = actionConfig?.icon

  const [isPristine, setIsPristine] = useState(true)
  const [error, setError] = useState(null)

  const [confirmData, setConfirmData] = useState({})
  const [isConfirmOpen, setConfirmOpen] = useState(false)

  const update = (data) => {
    const newState = { ...confirmData, ...data }
    setConfirmData(newState)
    setError(confirm?.validate?.(newState))
  }

  const [successData, setSuccessData] = useState(null)
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false)
  const [runAction, isRunningAction] = useRunResourceAction({
    action,
    actionName: actionConfig?.name,
    data: confirm?.toPayload ? confirm.toPayload(confirmData) : confirmData,
    id: record.id,
    onSuccess: (data) => {
      setSuccessData(data)
      setIsSuccessDialogOpen(true)
      setConfirmOpen(false)
    },
    showNotification: !SuccessDialog,
    notificationType: actionConfig?.notificationType,
  })

  const handleClick = () => {
    if (confirm) {
      setConfirmData(confirm.initialState ?? {})
      setIsPristine(true)
      setConfirmOpen(true)
    } else {
      runAction()
    }
  }
  const handleClose = () => setConfirmOpen(false)
  const handleConfirm = () => {
    setIsPristine(false)
    if (confirm.validate) {
      const error = confirm.validate(confirmData)
      if (error) {
        setError(error)
        return
      }
    }
    runAction()
  }

  return (
    <>
      <Button
        {...props}
        color={actionConfig?.color ?? 'primary'}
        variant="contained"
        onClick={handleClick}
        disabled={isRunningAction}
      >
        {Icon && <Icon sx={{ mr: '0.35em' }} />}
        {translate(actionLabel)}
      </Button>
      {confirm && (
        <Confirm
          cancel={confirm.cancelLabel}
          confirm={confirm.confirmLabel}
          confirmColor={confirm.confirmColor}
          content={confirm.content?.({ update, state: confirmData, isPristine, error })}
          isOpen={isConfirmOpen}
          loading={isRunningAction}
          onClose={handleClose}
          onConfirm={handleConfirm}
          title={actionConfig?.label}
        />
      )}
      {successData && SuccessDialog && (
        <SuccessDialog isOpen={isSuccessDialogOpen} setIsOpen={setIsSuccessDialogOpen} data={successData} />
      )}
    </>
  )
}

export default ActionButton
